import React, { useState, useEffect } from 'react';
import Logo from "../images/Infomanav.svg";
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader2 from "../component/Loader2";
import { Mail, Lock, ChevronRight, Check, RefreshCw, Eye, EyeOff } from 'lucide-react';
import "../css/ForgotPassword.css";

const ForgotPassword = ({ setProgress }) => {
  const pmsApiBaseUrl =
    process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";
  const [tabName, setTabName] = useState('Forgot Password');
  const [currentStep, setCurrentStep] = useState(1);
  const [passwordStrength, setPasswordStrength] = useState(0);

  useEffect(() => {
    document.title = tabName;
    if (setProgress) setProgress(100);
  }, [tabName, setProgress]);

 


  // Form state
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Resend OTP timer
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const navigate = useNavigate();

  // Password strength checker
const checkPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 20;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 20;
  if (/\d/.test(password)) strength += 20;
  if (/[^A-Za-z0-9]/.test(password)) strength += 20;
  return strength;
};

const getStrengthColor = () => {
  if (passwordStrength <= 40) return '#ef4444';
  if (passwordStrength <= 80) return '#f59e0b';
  return '#10b981';
};

const getStrengthText = () => {
  if (passwordStrength === 0) return formData.newPassword ? 'Too weak' : '';
  if (passwordStrength <= 40) return 'Weak';
  if (passwordStrength <= 80) return 'Medium';
  return 'Strong';
};

// useEffect to track password strength
useEffect(() => {
  if (formData.newPassword) {
    setPasswordStrength(checkPasswordStrength(formData.newPassword));
  } else {
    setPasswordStrength(0);
  }
}, [formData.newPassword]);



  // Toast options
  const toastOptions = {
    position: "top-center",
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
  };

  const showSuccessToast = (message) => {
    toast.success(message, { ...toastOptions, icon: "✅" });
  };

  const showErrorToast = (message) => {
    toast.error(message, { ...toastOptions, icon: "⚠️" });
  };

  const parsePmsResponse = async (response) => {
    try {
      const json = await response.json();
      return json?.data || {
        status: "failed",
        message: "Invalid response from server",
      };
    } catch (error) {
      console.error("PMS API parsing error:", error);
      return {
        status: "failed",
        message: "Invalid response from server",
      };
    }
  };

  // Resend timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Email validation
  const validateEmail = () => {
    const errors = {};
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // OTP validation
  const validateOTP = () => {
    const errors = {};
    if (!formData.otp.trim()) {
      errors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(formData.otp)) {
      errors.otp = 'OTP must be 6 digits';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Password validation
  const validatePassword = () => {
    const errors = {};
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    
    if (!formData.newPassword.trim()) {
      errors.newPassword = 'Password is required';
    } else if (!passwordRegex.test(formData.newPassword)) {
      errors.newPassword = 'Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character';
    }
    
    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Confirm password is required';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!validateEmail()) {
      showErrorToast('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${pmsApiBaseUrl}/api/v1/send-forgot-password-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      const data = await parsePmsResponse(response);

      if (data.status === 'success') {
        showSuccessToast(data.message || 'OTP sent successfully to your email');
        setCurrentStep(2);
        setResendTimer(30);
        setCanResend(false);
      } else {
        showErrorToast(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      showErrorToast('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (!canResend) return;

    setIsLoading(true);

    try {
      const response = await fetch(`${pmsApiBaseUrl}/api/v1/send-forgot-password-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      const data = await parsePmsResponse(response);

      if (data.status === 'success') {
        showSuccessToast('OTP resent successfully');
        setResendTimer(30);
        setCanResend(false);
        setFormData(prev => ({ ...prev, otp: '' }));
      } else {
        showErrorToast(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      showErrorToast('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Validate OTP
  const handleValidateOTP = async (e) => {
    e.preventDefault();
    
    if (!validateOTP()) {
      showErrorToast('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${pmsApiBaseUrl}/api/v1/validate-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email,
          otp: formData.otp 
        })
      });

      const data = await parsePmsResponse(response);

      if (data.status === 'success') {
        showSuccessToast(data.message || 'OTP validated successfully');
        setCurrentStep(3);
      } else {
        showErrorToast(data.message || 'Invalid OTP');
        
        // Check if OTP expired or max attempts exceeded
        if (data.message && (data.message.includes('expired') || data.message.includes('exceeded'))) {
          setTimeout(() => {
            setCurrentStep(1);
            setFormData({
              email: formData.email,
              otp: '',
              newPassword: '',
              confirmPassword: ''
            });
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Validate OTP error:', error);
      showErrorToast('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };




  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      showErrorToast('Please check password requirements');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${pmsApiBaseUrl}/api/v1/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email,
          new_password: formData.newPassword 
        })
      });

      const data = await parsePmsResponse(response);

      if (data.status === 'success') {
        showSuccessToast(data.message || 'Password reset successfully');
        
        setFormData({
          email: '',
          otp: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        setTimeout(() => {
          navigate('/');
          setIsLoading(false);
        }, 2000);
      } else {
        showErrorToast(data.message || 'Failed to reset password');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      showErrorToast('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const steps = [
    { id: 1, title: "Email" },
    { id: 2, title: "Code" },
    { id: 3, title: "Password" },
  ];

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="fp-step-content">
            <div className="fp-step-header">
              <h2 className="fp-step-title">Email</h2>
              <p className="fp-step-subtitle">We will send a one-time code to this address.</p>
            </div>

            <div className="fp-form-group">
              <label className="fp-label" htmlFor="fp-email">
                <Mail size={16} strokeWidth={2} aria-hidden />
                Email
              </label>
              <input
                id="fp-email"
                type="email"
                name="email"
                autoComplete="email"
                className={`fp-input ${formErrors.email ? 'fp-input-error' : ''}`}
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {formErrors.email && (
                <div className="fp-error-message">{formErrors.email}</div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="fp-step-content">
            <div className="fp-step-header">
              <h2 className="fp-step-title">Verification code</h2>
              <p className="fp-step-subtitle">
                Enter the 6-digit code sent to <span className="fp-email-chip">{formData.email}</span>
              </p>
            </div>

            <div className="fp-form-group">
              <label className="fp-label" htmlFor="fp-otp">
                <Check size={16} strokeWidth={2} aria-hidden />
                One-time code
              </label>
              <input
                id="fp-otp"
                type="text"
                name="otp"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                className={`fp-input fp-otp-input ${formErrors.otp ? 'fp-input-error' : ''}`}
                placeholder="••••••"
                maxLength="6"
                value={formData.otp}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {formErrors.otp && (
                <div className="fp-error-message">{formErrors.otp}</div>
              )}
            </div>

            <button
              type="button"
              className="fp-btn-secondary"
              onClick={handleResendOTP}
              disabled={!canResend || isLoading}
            >
              <RefreshCw size={16} strokeWidth={2} />
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
            </button>

            <button
              type="button"
              className="fp-change-email"
              onClick={() => setCurrentStep(1)}
            >
              Use a different email
            </button>
          </div>
        );

      case 3:
        return (
          <div className="fp-step-content">
            <div className="fp-step-header">
              <h2 className="fp-step-title">New password</h2>
              <p className="fp-step-subtitle">Use a strong password you have not used elsewhere.</p>
            </div>

            <div className="fp-pw-field">
              <label className="fp-pw-label" htmlFor="fp-new-password">New password</label>
              <div className={`fp-pw-control ${formErrors.newPassword ? 'fp-pw-control--error' : ''}`}>
                <span className="fp-pw-icon fp-pw-icon--left" aria-hidden>
                  <Lock size={18} strokeWidth={2} />
                </span>
                <input
                  id="fp-new-password"
                  type={showPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className="fp-pw-input"
                  placeholder="Create a password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="fp-pw-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
                </button>
              </div>
              {formErrors.newPassword && (
                <div className="fp-error-message">{formErrors.newPassword}</div>
              )}

              {formData.newPassword ? (
                <div className="fp-strength">
                  <div className="fp-strength-bars" role="presentation">
                    {[20, 40, 60, 80, 100].map((step) => (
                      <div
                        key={step}
                        className={`fp-strength-seg ${passwordStrength >= step ? 'fp-strength-seg--on' : ''}`}
                        style={
                          passwordStrength >= step
                            ? { backgroundColor: getStrengthColor() }
                            : undefined
                        }
                      />
                    ))}
                  </div>
                  <span className="fp-strength-label" style={{ color: getStrengthColor() }}>
                    {getStrengthText()} · {passwordStrength}%
                  </span>
                </div>
              ) : null}

              <div className="fp-req-box">
                <div className="fp-req-title">Password must include</div>
                <ul className="fp-req-list">
                  <li className={`fp-req-item ${formData.newPassword.length >= 8 ? 'fp-req-item--ok' : formData.newPassword ? 'fp-req-item--bad' : ''}`}>
                    At least 8 characters
                  </li>
                  <li className={`fp-req-item ${/[A-Z]/.test(formData.newPassword) && /[a-z]/.test(formData.newPassword) ? 'fp-req-item--ok' : formData.newPassword ? 'fp-req-item--bad' : ''}`}>
                    Upper and lower case letters
                  </li>
                  <li className={`fp-req-item ${/\d/.test(formData.newPassword) ? 'fp-req-item--ok' : formData.newPassword ? 'fp-req-item--bad' : ''}`}>
                    At least one number
                  </li>
                  <li className={`fp-req-item ${/[^A-Za-z0-9]/.test(formData.newPassword) ? 'fp-req-item--ok' : formData.newPassword ? 'fp-req-item--bad' : ''}`}>
                    At least one special character
                  </li>
                </ul>
              </div>
            </div>

            <div className="fp-pw-field">
              <label className="fp-pw-label" htmlFor="fp-confirm-password">Confirm password</label>
              <div className={`fp-pw-control ${formErrors.confirmPassword ? 'fp-pw-control--error' : ''}`}>
                <span className="fp-pw-icon fp-pw-icon--left" aria-hidden>
                  <Lock size={18} strokeWidth={2} />
                </span>
                <input
                  id="fp-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className="fp-pw-input"
                  placeholder="Re-enter password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="fp-pw-toggle"
                  onClick={toggleConfirmPasswordVisibility}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <div className="fp-error-message">{formErrors.confirmPassword}</div>
              )}
              {formData.confirmPassword && formData.newPassword === formData.confirmPassword ? (
                <div className="fp-pw-match">
                  <Check size={14} strokeWidth={2} aria-hidden />
                  <span>Passwords match</span>
                </div>
              ) : null}
            </div>
          </div>
        );





      default:
        return null;
    }
  };

  return (
    <>
      {/* <ToastContainer /> */}
      <ToastContainer 
        position="top-center"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        limit={3}
        />

        {isLoading && (
          <Loader2/>
        )}

      <div className="fp-page">
        <div className="fp-page__backdrop" aria-hidden />

        <main className="fp-page__main">
          <div className="fp-page__shell">
            <header className="fp-page__brand">
              <img src={Logo} alt="Infomanav" className="fp-page__logo" />
              <h1 className="fp-page__title">Reset password</h1>
              <p className="fp-page__lede">Enter your email, confirm the code, then set a new password.</p>
            </header>

            <div className="fp-card">
              <div className="fp-stepper" aria-label="Reset progress">
                <div className="fp-stepper__dots-row">
                  <div className="fp-stepper__line-track" aria-hidden>
                    <div
                      className="fp-stepper__line-fill"
                      style={{ width: `${(currentStep / steps.length) * 100}%` }}
                    />
                  </div>
                  <ol className="fp-stepper__dots-list">
                    {steps.map((step) => (
                      <li
                        key={step.id}
                        className={`fp-stepper__dot-cell ${currentStep === step.id ? 'fp-stepper__item--current' : ''} ${currentStep > step.id ? 'fp-stepper__item--done' : ''}`}
                      >
                        <span className="fp-stepper__dot" aria-hidden>
                          {currentStep > step.id ? <Check size={14} strokeWidth={2.5} /> : step.id}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="fp-stepper__labels-row">
                  {steps.map((step) => (
                    <span
                      key={step.id}
                      className={`fp-stepper__label ${currentStep === step.id ? 'fp-stepper__item--current' : ''} ${currentStep > step.id ? 'fp-stepper__item--done' : ''}`}
                    >
                      {step.title}
                    </span>
                  ))}
                </div>
              </div>

              <form
                className="fp-form"
                onSubmit={
                  currentStep === 1 ? handleSendOTP :
                  currentStep === 2 ? handleValidateOTP :
                  handleResetPassword
                }
              >
                {renderStepContent()}

                <button type="submit" className="fp-btn-primary" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="fp-spinner" aria-hidden />
                      Please wait…
                    </>
                  ) : (
                    <>
                      {currentStep === 1 ? 'Send code' :
                       currentStep === 2 ? 'Verify code' :
                       'Update password'}
                      <ChevronRight size={18} strokeWidth={2} aria-hidden />
                    </>
                  )}
                </button>
              </form>
            </div>

            <p className="fp-page__footer">
              <Link to="/" className="fp-page__link">Back to sign in</Link>
            </p>
          </div>
        </main>
      </div>
    </>
  );
};

export default ForgotPassword;
